import { I18n } from '@calendly/i18n';
import { DropdownMenuGroup, DropdownMenuItem } from '@calendly/ui/components/dropdown-menu';
import { IconDevices } from '@calendly/ui/components/glyphs/IconDevices';
import { IconMoon } from '@calendly/ui/components/glyphs/IconMoon';
import { IconSun } from '@calendly/ui/components/glyphs/IconSun';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import { type UiIconGlyphComponent } from '@calendly/ui/components/ui-icon';
import { ViewSwitcherIcon } from '@calendly/ui/components/view-switcher';
import { useCallback, useId, useMemo, type ChangeEvent, type ComponentProps, type ComponentType, type FC, type KeyboardEvent, type KeyboardEventHandler } from 'react';
import { THEME_PREFERENCES, useThemePreference, type ThemePreference } from 'features/app_shell/shell_layout/themePreferenceContext';

/**
 * Radio-group name for the theme options.
 * The browser groups radios by name, so sharing this value with another switcher on screen would merge
 * them into a single control.
 */
const THEME_SWITCHER_GROUP_NAME = 'shell-profile-theme';
export const PROFILE_THEME_ROW_TEST_IDS = {
  row: 'profile-menu-theme-row',
  value: 'profile-menu-theme-value',
  switcher: 'profile-menu-theme-switcher'
} as const;
export const PROFILE_THEME_ROW_I18N_KEYS = {
  label: 'publisher.app_shell.profile_menu.theme.label',
  light: 'publisher.app_shell.profile_menu.theme.light',
  dark: 'publisher.app_shell.profile_menu.theme.dark',
  system: 'publisher.app_shell.profile_menu.theme.system'
} as const;
type ThemeSwitcherOption = {
  /** Theme persisted when this option is chosen. */
  value: ThemePreference;
  /** Localized option name; also serves as the hover tooltip and the option's accessible name. */
  text: string;
  /** Glyph representing the option in the icon-only control. */
  icon: UiIconGlyphComponent;
};

/**
 * A menu row that also accepts a key handler.
 * Menu rows already pass keys they do not act on themselves through to `onKeyDown`, but the published prop
 * type leaves it out, so the row cannot ask for its own arrow-key handling without widening the type.
 */
type ThemeRowMenuItemProps = ComponentProps<typeof DropdownMenuItem> & {
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
};
const ThemeRowMenuItem = DropdownMenuItem as ComponentType<ThemeRowMenuItemProps>;

/**
 * Pairing of the static label with the selected theme.
 * Long translations wrap onto a second line rather than being truncated.
 */
const themeLabelGroupCss = "t11sat53";

/** Groups the theme options and right-aligns them in the row. */
const themeSwitcherGroupCss = "t16m6rni";

/**
 * Theme switcher for the profile menu, letting a user follow their device or pin light or dark.
 *
 * Renders as its own menu section so the menu draws a separator above and below the row, and the choice is
 * remembered per browser.
 *
 * The row is a menu row rather than inert content so it can be reached with the same up and down keys as
 * every other row; left and right then move between the themes. Activating the row itself intentionally
 * does nothing and leaves the menu open.
 *
 * Must be rendered inside `DropdownMenuContent`.
 *
 * @example
 * <DropdownMenuContent>
 *   <DropdownMenuGroup>
 *     <DropdownMenuItem onSelect={goProfile}>Profile</DropdownMenuItem>
 *   </DropdownMenuGroup>
 *   <ProfileThemeRow />
 * </DropdownMenuContent>
 */
export const ProfileThemeRow: FC = () => {
  const {
    themePreference,
    setThemePreference
  } = useThemePreference();
  /* Ties the label to the switcher so the group is announced by name. */
  const themeLabelId = useId();
  const themeOptions = useMemo<ThemeSwitcherOption[]>(() => [{
    value: THEME_PREFERENCES.light,
    text: I18n.t(PROFILE_THEME_ROW_I18N_KEYS.light),
    icon: IconSun
  }, {
    value: THEME_PREFERENCES.dark,
    text: I18n.t(PROFILE_THEME_ROW_I18N_KEYS.dark),
    icon: IconMoon
  }, {
    value: THEME_PREFERENCES.system,
    text: I18n.t(PROFILE_THEME_ROW_I18N_KEYS.system),
    icon: IconDevices
  }], []);
  const themeLabel = I18n.t(PROFILE_THEME_ROW_I18N_KEYS.label);
  const selectedOptionText = themeOptions.find(option => option.value === themePreference)?.text;

  /* Resolve the event value against the known options so an unexpected value is ignored. */
  const handleThemeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selected = themeOptions.find(option => option.value === event.target.value);
    if (selected) {
      setThemePreference(selected.value);
    }
  }, [setThemePreference, themeOptions]);

  /** Moves the selection by one option, wrapping at either end. */
  const handleRowKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    /* Also suppresses the options' own left/right handling, which would otherwise move a second time. */
    event.preventDefault();
    const currentIndex = themeOptions.findIndex(option => option.value === themePreference);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + step + themeOptions.length) % themeOptions.length;
    setThemePreference(themeOptions[nextIndex].value);
  }, [setThemePreference, themeOptions, themePreference]);
  return <DropdownMenuGroup>
      <ThemeRowMenuItem disableAutoClose aria-label={`${themeLabel} ${selectedOptionText}`} onKeyDown={handleRowKeyDown} data-testid={PROFILE_THEME_ROW_TEST_IDS.row}>
        <span className={themeLabelGroupCss}>
          <LabelText id={themeLabelId} size="large">
            {themeLabel}
          </LabelText>
          <BodyText inline size="medium" weight="regular" aria-live="polite" data-testid={PROFILE_THEME_ROW_TEST_IDS.value}>
            {selectedOptionText}
          </BodyText>
        </span>
        <span className={themeSwitcherGroupCss} role="radiogroup" aria-labelledby={themeLabelId} data-testid={PROFILE_THEME_ROW_TEST_IDS.switcher}>
          <ViewSwitcherIcon name={THEME_SWITCHER_GROUP_NAME} size="small" value={themePreference} onChange={handleThemeChange} options={themeOptions} />
        </span>
      </ThemeRowMenuItem>
    </DropdownMenuGroup>;
};

require("./ProfileThemeRow.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ProfileThemeRow.tsx");